import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Make + Model are strict dropdowns sourced from the configured car brands (Settings ->
// Configuration). Model options depend on the selected Make. Colour + plate stay free text.
// `value` = { make, model, colour, plate }.
export const VehicleFields = ({ value, onChange, testidPrefix = "veh" }) => {
  const [brands, setBrands] = useState([]);
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });

  useEffect(() => {
    api.get("/vehicle-brands").then(({ data }) => setBrands(data || [])).catch(() => setBrands([]));
  }, []);

  const brand = brands.find((b) => b.name === v.make);
  const isBike = (v.make || "") === "Bike";
  // Keep legacy/free-typed values selectable so existing leads still display correctly.
  const makeOptions = v.make && v.make !== "Bike" && !brands.some((b) => b.name === v.make)
    ? [{ name: v.make, models: [] }, ...brands] : brands;
  const modelList = brand?.models || [];
  const modelOptions = v.model && !modelList.includes(v.model)
    ? [v.model, ...modelList] : modelList;

  const onMakeChange = (name) => {
    if (name === "Bike") {
      onChange({ ...v, make: "Bike", model: "" }); // model becomes free text
      return;
    }
    const b = brands.find((x) => x.name === name);
    // Reset model when the make changes to a brand that doesn't have the current model.
    const keepModel = b?.models?.includes(v.model) ? v.model : "";
    onChange({ ...v, make: name, model: keepModel });
  };

  return (
    <>
      <div>
        <Label className="text-xs">Make</Label>
        <Select value={v.make || ""} onValueChange={onMakeChange}>
          <SelectTrigger className="mt-1" data-testid={`${testidPrefix}-make`}>
            <SelectValue placeholder="Select make" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Bike" className="font-bold text-brand-ink" data-testid={`${testidPrefix}-make-bike`}>
              Bike — type model
            </SelectItem>
            <div className="my-1 border-t border-brand-line" />
            {makeOptions.map((b) => (
              <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Model</Label>
        {isBike ? (
          <Input
            className="mt-1"
            data-testid={`${testidPrefix}-model-input`}
            placeholder="Type the bike name"
            value={v.model || ""}
            onChange={(e) => set("model", e.target.value)}
          />
        ) : (
          <Select value={v.model || ""} onValueChange={(m) => set("model", m)} disabled={!v.make}>
            <SelectTrigger className="mt-1" data-testid={`${testidPrefix}-model`}>
              <SelectValue placeholder={v.make ? "Select model" : "Select make first"} />
            </SelectTrigger>
            <SelectContent>
              {modelOptions.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-400">No models configured for this make</div>
              ) : modelOptions.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <div>
        <Label className="text-xs">Colour</Label>
        <Input className="mt-1" data-testid={`${testidPrefix}-colour`}
               value={v.colour || ""} onChange={(e) => set("colour", e.target.value)} />
      </div>
      <div>
        <Label className="text-xs">Number plate</Label>
        <Input className="mt-1" data-testid={`${testidPrefix}-plate`}
               value={v.plate || ""} onChange={(e) => set("plate", e.target.value)} />
      </div>
    </>
  );
};
